import {
	createTarotCard,
	stringToTarotDeck,
	tarotCardIdToCardInfo,
	TarotDeckSchema,
	TarotDeckSchema_latest,
} from "@/domain/cards";
import { randomUUID } from "crypto";
import { createContext } from "react";
import { z } from "zod/v4";
import { getCloudValues, setCloudValue } from "./cloud-storage";
import { blueprint } from "./deck.helpers";

type TarotDeck_latest = z.infer<typeof TarotDeckSchema_latest>;

type OnReadyHandler = (tarotDeckHandler: TarotDeckHandler) => void;
type OnErrorHandler = (error: "not-deck-holder") => void;
export class TarotDeckHandler {
	private deckHolderId: string;
	private latestShuffle: Promise<{ unsafe_deck: TarotDeck_latest }>;
	meta: Promise<Pick<TarotDeck_latest, "id" | "createdAt" | "lastShuffled">>;

	static getCardsFromDeck(deck: TarotDeck_latest, ...idxs: number[]) {
		return idxs.map((idx) => {
			const card = deck.cards.at(idx);

			if (!card) return null;

			return {
				...tarotCardIdToCardInfo.decode(card[0]),
				isUpright: card[1],
			};
		});
	}

	static create(onReady: OnReadyHandler, onError: OnErrorHandler): void {
		if (
			"tarotDeckHandler" in window &&
			window.tarotDeckHandler instanceof TarotDeckHandler
		) {
			onReady(window.tarotDeckHandler);
		} else {
			new TarotDeckHandler((deckHandler) => {
				// @ts-expect-error
				window.tarotDeckHandler = deckHandler;

				onReady(deckHandler);
			}, onError);
		}
	}

	constructor(
		onReady: OnReadyHandler,
		private onError: OnErrorHandler,
	) {
		this.deckHolderId = randomUUID();
		this.latestShuffle = this.init().then((deck) => ({ unsafe_deck: deck }));
		this.meta = this.latestShuffle.then(({ unsafe_deck }) => ({
			id: unsafe_deck.id,
			createdAt: unsafe_deck.createdAt,
			lastShuffled: unsafe_deck.lastShuffled,
		}));
		this.latestShuffle.then(() => onReady(this));
	}

	async shuffle(): typeof this.latestShuffle {
		/** This way we synchronously update the latest shuffle */
		this.latestShuffle = this.latestShuffle
			.then(() => this.getDeck())
			.then((deck) => {
				const newSpread = [...deck.cards];

				for (let i = newSpread.length - 1; i > 0; i--) {
					const j = Math.floor(Math.random() * (i + 1));
					const [cardI] = newSpread[i]!;
					const [cardJ] = newSpread[j]!;

					newSpread[i] = [cardJ, Math.random() < 0.5];
					newSpread[j] = [cardI, Math.random() < 0.5];
				}

				return this.commitDeck({
					...deck,
					v: 2,
					cards: newSpread,
					lastShuffled: new Date().toISOString(),
				}).then((deck) => ({ unsafe_deck: deck }));
			});

		return this.latestShuffle;
	}

	async getCardsByIdx(...idxs: number[]) {
		await this.latestShuffle;

		const deck = await this.getDeck();

		return TarotDeckHandler.getCardsFromDeck(deck, ...idxs);
	}

	private async init() {
		await setCloudValue("current-deck-holder", this.deckHolderId);
		return this.getDeck();
	}

	private async getDeck(): Promise<TarotDeck_latest> {
		const {
			"current-deck-holder": currentDeckHolder,
			"classic-deck": deckRaw,
		} = (await getCloudValues(["current-deck-holder", "classic-deck"])) ?? {};

		if (currentDeckHolder !== this.deckHolderId) {
			this.onError("not-deck-holder");
			throw new Error("Not a deck holder!");
		}

		const currentDeck = deckRaw
			? stringToTarotDeck.decode(deckRaw)
			: await this.createDeck();

		return this.migrate(currentDeck);
	}

	private async commitDeck(deck: TarotDeck_latest): Promise<TarotDeck_latest> {
		await setCloudValue("classic-deck", stringToTarotDeck.encode(deck));

		return deck;
	}

	private async createDeck(): Promise<TarotDeck_latest> {
		const id = Date.now().toString(36);
		const newDeck = {
			id,
			v: 2 as const,
			cards: blueprint.map<[string, boolean]>((card) => [
				createTarotCard(id, card.name),
				true,
			]),
			createdAt: new Date().toISOString(),
		};

		return this.commitDeck(newDeck);
	}

	private async migrate(
		deck: z.infer<typeof TarotDeckSchema>,
	): Promise<TarotDeck_latest> {
		if (deck.v === undefined) return this.createDeck();

		return deck;
	}
}

export const TarotDeckHandlerContext = createContext<TarotDeckHandler>(
	{} as any,
);
